// crewbit-admin/src/hooks/useMoneyActivityFeed.ts
//
// Arc C3 P2 — wiring for §Money.RecentActivitySeeAllDestination.
// Mirrors crewbit-mobile/hooks/useMoneyActivityFeed.ts.
// Spec: docs/references/money/2026-05-31-C3-Money-Nav-Surface-Polish-Spec-v1.md §5 (P2).
//
// Paginated full Money activity feed over the P1 RPC get_money_activity. Reads
// the same source as financial_summary.recent_activity (activity_log) and
// reuses FinancialSummaryActivityItem. Web-runtime divergence: window event
// listener instead of DeviceEventEmitter. Offset is internal (append via
// loadMore); callers set page size + filters.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { callMoneyRpc } from '../lib/moneyRpc';
import type { MoneyError } from '../lib/errorMap';
import {
  MONEY_MUTATION_EVENT,
  type FinancialSummaryActivityItem,
} from './useFinancialSummary';
import { useActiveBusinessStore } from './useActiveBusiness';

export type MoneyActivityType = 'in' | 'out';

export interface UseMoneyActivityFeedArgs {
  limit?: number;
  type?: MoneyActivityType | null;
  from?: string | null; // YYYY-MM-DD
  to?: string | null; // YYYY-MM-DD
}

export interface UseMoneyActivityFeedReturn {
  rows: FinancialSummaryActivityItem[];
  total: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: MoneyError | null;
  loadMore: () => void;
  refetch: () => Promise<void>;
}

interface RawRow {
  id?: string;
  activity_type: string;
  description: string | null;
  entity_type: string | null;
  entity_id: string | null;
  amount: number | null;
  direction: 'in' | 'out' | null;
  created_at: string;
}

interface RawEnvelope {
  success: boolean;
  rows?: RawRow[];
  total?: number;
  has_more?: boolean;
}

const DEFAULT_LIMIT = 25;

function normalizeRow(row: RawRow, idx: number): FinancialSummaryActivityItem {
  return {
    id:
      row.id ??
      `${row.entity_type ?? 'none'}-${row.entity_id ?? 'none'}-${row.activity_type}-${row.created_at}-${idx}`,
    activity_type: row.activity_type,
    description: row.description,
    entity_type: row.entity_type,
    entity_id: row.entity_id,
    amount: row.amount ?? null,
    direction: row.direction,
    created_at: row.created_at,
  };
}

export function useMoneyActivityFeed({
  limit = DEFAULT_LIMIT,
  type = null,
  from = null,
  to = null,
}: UseMoneyActivityFeedArgs = {}): UseMoneyActivityFeedReturn {
  const activeBusinessId = useActiveBusinessStore((s) => s.activeBusinessId);

  const [rows, setRows] = useState<FinancialSummaryActivityItem[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<MoneyError | null>(null);

  const rowsRef = useRef<FinancialSummaryActivityItem[]>([]);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchPage = useCallback(
    async (reset: boolean) => {
      if (!activeBusinessId) return;
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      const offset = reset ? 0 : rowsRef.current.length;
      if (reset) setIsLoading(true);
      else setIsLoadingMore(true);
      try {
        const result = await callMoneyRpc<RawEnvelope>('get_money_activity', {
          p_limit: limit,
          p_offset: offset,
          p_type: type,
          p_from: from,
          p_to: to,
        });
        if (!mountedRef.current) return;
        if (result.success) {
          const incoming = (result.data.rows ?? []).map(normalizeRow);
          setRows(reset ? incoming : [...rowsRef.current, ...incoming]);
          setTotal(result.data.total ?? 0);
          setHasMore(Boolean(result.data.has_more));
          setError(null);
        } else {
          setError(result.error);
        }
      } finally {
        if (mountedRef.current) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
        inFlightRef.current = false;
      }
    },
    [activeBusinessId, limit, type, from, to],
  );

  useEffect(() => {
    setRows([]);
    setTotal(0);
    setHasMore(false);
    setError(null);
  }, [activeBusinessId, limit, type, from, to]);

  useEffect(() => {
    if (!activeBusinessId) return;
    void fetchPage(true);
  }, [activeBusinessId, limit, type, from, to, fetchPage]);

  useEffect(() => {
    const onMutation = () => {
      void fetchPage(true);
    };
    window.addEventListener(MONEY_MUTATION_EVENT, onMutation);
    return () => {
      window.removeEventListener(MONEY_MUTATION_EVENT, onMutation);
    };
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (inFlightRef.current || !hasMore) return;
    void fetchPage(false);
  }, [hasMore, fetchPage]);

  const refetch = useCallback(async () => {
    await fetchPage(true);
  }, [fetchPage]);

  return useMemo(
    () => ({ rows, total, hasMore, isLoading, isLoadingMore, error, loadMore, refetch }),
    [rows, total, hasMore, isLoading, isLoadingMore, error, loadMore, refetch],
  );
}
